import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheck, FaArrowLeft, FaArrowRight, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Pantalla Fit Evolution que muestra la recomendación del tipo de alimentación.
 * Recibe las respuestas del formulario vía location.state.answers y llama al
 * motor de recomendación del backend. El usuario puede aceptar o explorar
 * y elegir otra alternativa.
 */
export default function FitEvolutionDietRecommendation({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Answers pueden venir por state (navegación con estado) o por sessionStorage
  // (redirect completo desde el submit del formulario).
  let answersFromSession = {};
  try {
    const raw = sessionStorage.getItem('fit_evo_answers');
    if (raw) answersFromSession = JSON.parse(raw) || {};
  } catch { /* sessionStorage no disponible o payload corrupto - se ignora */ }
  const answers = location.state?.answers || answersFromSession;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reco, setReco] = useState(null); // resultado del backend
  const [selectedId, setSelectedId] = useState(null);
  const [exploring, setExploring] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/api/personalized-plans/recommend-diet-type`, {
          metabolism: answers.metabolism || null,
          hunger_level: answers.hunger_level || null,
          water_retention: answers.water_retention || null,
          sleep_quality: answers.sleep_quality || null,
          objective: answers.objective || null,
          activity_level: answers.activity_level || null,
          pathologies: answers.pathologies || []
        });
        if (cancelled) return;
        setReco(res.data);
        setSelectedId(res.data.recommended_id);
      } catch (err) {
        if (cancelled) return;
        setError('No pudimos calcular tu recomendación. Puedes intentar de nuevo.');
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [answers]);

  const confirm = async () => {
    try {
      // Guardar la elección del usuario en su perfil (best-effort)
      const email = user?.email || answers.email;
      if (email) {
        await axios.post(`${API}/api/personalized-plans/save-diet-type`, {
          email, diet_type_id: selectedId
        }).catch(() => {});
      }
      toast.success('¡Perfecto! Tu tipo de alimentación quedó registrado.');
      navigate('/dashboard');
    } catch {
      toast.error('No pudimos guardar la elección, pero puedes continuar.');
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="text-center text-white">
          <div className="animate-spin w-10 h-10 border-2 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-sm text-gray-300">Analizando tus respuestas...</p>
        </div>
      </div>
    );
  }
  if (error || !reco) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="text-center text-white max-w-md">
          <p className="mb-4">{error || 'Sin datos'}</p>
          <Button onClick={() => navigate(-1)} variant="outline" className="text-white border-white/30">Volver</Button>
        </div>
      </div>
    );
  }

  const recommended = reco.recommended;
  const allTypes = reco.all_types || [];
  const selectedType = allTypes.find(t => t.id === selectedId) || recommended;

  return (
    <div className="min-h-screen py-8 px-4 md:px-6" style={{ backgroundColor: '#0a0a0a' }} data-testid="fit-evo-diet-recommendation">
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 15% 85%, rgba(var(--brand-primary-glow),0.14), transparent 55%), radial-gradient(circle at 85% 15%, rgba(0,240,255,0.10), transparent 45%)' }}
      />
      <div className="max-w-3xl mx-auto relative z-10">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-white/70 hover:text-white mb-6 hover:bg-white/10">
          <FaArrowLeft className="mr-2" /> Volver
        </Button>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] mb-2" style={{ color: 'var(--brand-primary)' }}>
            Fit Evolution · Recomendación personalizada
          </p>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-3 uppercase tracking-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Tu tipo de alimentación ideal
          </h1>
          <p className="text-gray-300 text-sm md:text-base max-w-lg mx-auto">
            Analizamos tus respuestas para encontrar la distribución que mejor se adapta a ti.
          </p>
        </motion.div>

        {/* Card recomendación principal */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedType.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="rounded-3xl p-6 md:p-8 mb-6"
            style={{
              background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow),0.14), rgba(0,240,255,0.06))',
              border: '2px solid rgba(var(--brand-primary-glow),0.4)',
              boxShadow: '0 0 40px rgba(var(--brand-primary-glow),0.15)'
            }}
          >
            <div className="flex items-start justify-between mb-4 gap-3">
              <div>
                {selectedType.id === recommended.id && (
                  <span className="text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full text-white inline-block mb-2" style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}>
                    ✓ Recomendada para ti
                  </span>
                )}
                <h2 className="text-2xl md:text-3xl font-black text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {selectedType.emoji} {selectedType.name}
                </h2>
              </div>
            </div>

            {/* Macros */}
            <div className="grid grid-cols-3 gap-3 mb-6" data-testid="diet-macros">
              {[
                { l: 'Proteínas', v: selectedType.macros.protein, c: 'var(--brand-primary)' },
                { l: 'Carbohidratos', v: selectedType.macros.carbs, c: '#00F0FF' },
                { l: 'Grasas', v: selectedType.macros.fats, c: '#FDE047' }
              ].map((m, i) => (
                <div key={i} className="rounded-xl p-3 text-center" style={{ background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">{m.l}</p>
                  <p className="text-2xl font-black" style={{ color: m.c, fontFamily: 'Poppins, sans-serif' }}>{m.v}%</p>
                </div>
              ))}
            </div>

            {/* Personal reasons - solo cuando estoy en la recomendada */}
            {selectedType.id === recommended.id && recommended.personal_reasons?.length > 0 && (
              <div className="mb-5 rounded-xl p-4" style={{ background: 'rgba(0,240,255,0.06)', border: '1px solid rgba(0,240,255,0.25)' }}>
                <p className="text-[11px] font-bold uppercase tracking-widest text-cyan-300 mb-2">¿Por qué te la recomendamos?</p>
                <ul className="space-y-1.5">
                  {recommended.personal_reasons.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/90"><FaCheck className="text-cyan-400 mt-1 text-[10px]" /><span>{r}</span></li>
                  ))}
                </ul>
              </div>
            )}

            {/* Info expandible */}
            <Section title="¿Para quién es?" content={selectedType.for_whom} id="for_whom" expanded={expanded} setExpanded={setExpanded} />
            {selectedType.when_it_works && <Section title="¿Cuándo funciona mejor?" content={selectedType.when_it_works} id="when_it_works" expanded={expanded} setExpanded={setExpanded} />}
            <Section title="Beneficios principales" items={selectedType.benefits} id="benefits" expanded={expanded} setExpanded={setExpanded} />
            <Section title="Posibles limitaciones" items={selectedType.limitations} id="limitations" expanded={expanded} setExpanded={setExpanded} />
          </motion.div>
        </AnimatePresence>

        {/* Botón para explorar alternativas */}
        <div className="mb-6">
          <button
            onClick={() => setExploring(!exploring)}
            className="w-full text-center text-sm text-white/70 hover:text-white py-3 rounded-xl transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            data-testid="toggle-alternatives"
          >
            {exploring ? 'Ocultar alternativas' : 'Ver otras alternativas'} {exploring ? '↑' : '↓'}
          </button>
          {exploring && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              {allTypes.filter(t => t.id !== selectedType.id).map(t => (
                <button
                  key={t.id}
                  onClick={() => setSelectedId(t.id)}
                  className="text-left rounded-xl p-4 hover:scale-[1.01] transition-all"
                  style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.12)' }}
                  data-testid={`alt-${t.id}`}
                >
                  <p className="text-white font-bold text-sm mb-1">{t.emoji} {t.name}</p>
                  <p className="text-[11px] text-gray-400">P {t.macros.protein}% · C {t.macros.carbs}% · G {t.macros.fats}%</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="flex flex-col md:flex-row gap-3 max-w-md mx-auto">
          <Button
            onClick={confirm}
            className="flex-1 rounded-full font-bold text-white text-base py-6 hover:scale-[1.01] transition-all"
            style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
            data-testid="confirm-diet-type"
          >
            Continuar con {selectedType.name} <FaArrowRight className="ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, content, items, id, expanded, setExpanded }) {
  const isOpen = expanded[id] ?? true;
  return (
    <div className="mb-3 rounded-xl overflow-hidden" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)' }}>
      <button onClick={() => setExpanded({ ...expanded, [id]: !isOpen })} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/5 transition-all">
        <p className="text-[11px] font-bold uppercase tracking-widest text-pink-300/90">{title}</p>
        {isOpen ? <FaChevronUp className="text-white/50 text-xs" /> : <FaChevronDown className="text-white/50 text-xs" />}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-1">
          {content && <p className="text-sm text-white/85 leading-relaxed">{content}</p>}
          {items && (
            <ul className="space-y-1.5">
              {items.map((it, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-white/85"><FaCheck className="text-pink-400 mt-1 text-[10px]" /><span>{it}</span></li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
